// Settings > Printing Setup (ShipStation pattern). Mock-only V1.
// M3 Stage 4 SPEC.md Faz 3 = real PDF generation + label rendering + printer integration backend.
// Volkan direction 2026-05-23 — 4 reference screenshots.
//
// Surface-aware: only admin renders the full options matrix. Seller / supply
// shells render a read-only placeholder per D-7 surface segregation (print
// operations live in the admin scope — admin-owned resource per D-6).
//
// Schema hints (DRAFT — Stage 4 SPEC.md Faz 3 print sub-cluster ratify):
//   document_type_preferences  account-wide routing prefs
//   label_layout_settings      account-wide layout + DPI + cutoff
//   packing_slip_settings      account-wide rendering toggles
//   order_summary_settings     account-wide rendering toggles
//   print_warning_settings     per-machine warnings (mock collapsed)
//
// i18n Wave-2 Day 4 wire: all customer-facing chrome consumes
// printingSetupI18n via useT. Dropdown option labels stay sourced from
// mocks/data (single source-of-truth for select binding; option labels
// are reference / ShipStation parity and not customer-locale-sensitive).

import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Check,
  ExternalLink,
  Info,
  Printer,
  Settings as SettingsIcon,
} from 'lucide-react'
import { Button } from '../../components/ui/button'
import {
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog'
import { Dialog } from '../../components/ui/dialog.lib'
import { useT } from '../../i18n'
import { printingSetupI18n } from '../../i18n/settings/printing-setup'
import { apiUrl, USE_REAL_API } from '../../lib/api'
import { toast } from '../../lib/toast'
import {
  openPrintingPreview,
  type SampleCarrier,
} from '../admin/order-documents'
import {
  BATCH_SUMMARY_OPTIONS,
  LABEL_DPI_OPTIONS,
  LABEL_SIZE_TABS,
  ORDER_LABELS_BY_OPTIONS,
  ORDER_SUMMARY_SORT_OPTIONS,
  PACKING_SLIP_FORMAT_OPTIONS,
  PRINT_TO_OPTIONS,
  SORT_ITEMS_BY_OPTIONS,
  type DocumentType,
  type DocumentTypePref,
  type LabelLayoutSettings,
  type LabelSize,
  type OrderSummarySettings,
  type PackingSlipSettings,
  type PrintTo,
  type PrintWarningSettings,
  type PrintingSettingsPayload,
} from '../../lib/printing-options'
import type { SettingsSurface } from './Settings'
import { DataTableShell } from '../../components/ui/data-table-shell'

// ---------------------------------------------------------------------------
// Shared inputs styled to match the seller dashboard form palette. Native
// <select>'s keep the Tailwind v4 footprint minimal while matching existing
// Wallet sub-page conventions.
// ---------------------------------------------------------------------------

const INPUT_CLS =
  'w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 focus:border-brand-400 focus:outline-none focus:ring-1 focus:ring-brand-200'

const SELECT_CLS = INPUT_CLS + ' pr-8'

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-slate-500">
      {children}
    </label>
  )
}

function SectionTitle({
  children,
  hint,
}: {
  children: React.ReactNode
  hint?: string
}) {
  return (
    <div className="mb-2">
      <h3 className="text-sm font-semibold text-slate-900">{children}</h3>
      {hint && <p className="mt-0.5 text-xs text-slate-500">{hint}</p>}
    </div>
  )
}

// Toggle row matching the existing PEXIFY UI vibe (slate / sky palette).
function Toggle({
  label,
  description,
  checked,
  onChange,
}: {
  label: string
  description?: string
  checked: boolean
  onChange: (next: boolean) => void
}) {
  return (
    <div className="flex items-start justify-between gap-4 py-2.5">
      <div className="flex-1">
        <div className="text-sm font-medium text-slate-800">{label}</div>
        {description && (
          <div className="mt-0.5 text-xs text-slate-500">{description}</div>
        )}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={
          'relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full transition-colors ' +
          (checked ? 'bg-brand-600' : 'bg-slate-300')
        }
      >
        <span
          className={
            'inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ' +
            (checked ? 'translate-x-4' : 'translate-x-0.5')
          }
        />
      </button>
    </div>
  )
}

// Checkbox row matching the order summary screenshot — labelled checkbox on
// a single line with optional description below.
function CheckboxRow({
  label,
  description,
  checked,
  onChange,
}: {
  label: string
  description?: string
  checked: boolean
  onChange: (next: boolean) => void
}) {
  return (
    <label className="flex cursor-pointer items-start gap-2.5 py-2">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-400"
      />
      <div>
        <div className="text-sm text-slate-800">{label}</div>
        {description && (
          <div className="mt-0.5 text-xs text-slate-500">{description}</div>
        )}
      </div>
    </label>
  )
}

// ---------------------------------------------------------------------------
// Network layer — TanStack Query bindings against MSW endpoints.
// ---------------------------------------------------------------------------

// A message is a template: fields expand at purchase, and the carrier prints
// the first 26 characters of the result.
const MESSAGE_TEMPLATE_MAX = 60
const LABEL_MESSAGE_TOKENS = [
  '[Order #]',
  '[Custom Field #1]',
  '[Custom Field #2]',
  '[Custom Field #3]',
  '[Store Company Name]',
  '[Buyer Name]',
  '[Order Date]',
  '[Ship Date]',
  '[Sku #1]',
  '[Product #1]',
  '[Warehouse Loc #1]',
  '[Gift Note]',
] as const

const PRINTING_QUERY_KEY = ['settings', 'printing'] as const

async function fetchPrintingSettings(): Promise<PrintingSettingsPayload> {
  const res = await fetch(apiUrl('/settings/printing'))
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  return res.json()
}

async function patchPrintingSettings(
  patch: Partial<PrintingSettingsPayload>,
): Promise<PrintingSettingsPayload> {
  const res = await fetch(apiUrl('/settings/printing'), {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  })
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  return res.json()
}

// ---------------------------------------------------------------------------
// Non-admin surface placeholder. D-7 + D-6 — print operations are an admin
// owned resource; seller / supply orgs see informational stub only.
// ---------------------------------------------------------------------------

function PrintingPlaceholder({ surface }: { surface: SettingsSurface }) {
  const t = useT(printingSetupI18n)
  const surfaceWord = surface === 'seller' ? 'Seller' : 'Supply'
  return (
    <div className="rounded-lg border border-dashed border-slate-300 bg-white p-8 text-center">
      <Printer className="mx-auto h-8 w-8 text-slate-300" />
      <h2 className="mt-3 text-lg font-semibold text-slate-900">
        {t.placeholder.title}
      </h2>
      <p className="mx-auto mt-2 max-w-md text-sm text-slate-500">
        {t.placeholder.description(surfaceWord)}
      </p>
      <p className="mt-4 text-xs text-slate-400">{t.placeholder.mockFooter}</p>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main entry — admin matrix + warning toggles + supplies upsell card.
// ---------------------------------------------------------------------------

type ModalKind = 'label' | 'packing_slip' | 'order_summary' | null

export function PrintingSetup({ surface }: { surface: SettingsSurface }) {
  const t = useT(printingSetupI18n)
  const queryClient = useQueryClient()
  const [modal, setModal] = useState<ModalKind>(null)

  const { data, isLoading, isError } = useQuery<PrintingSettingsPayload>({
    queryKey: PRINTING_QUERY_KEY,
    queryFn: fetchPrintingSettings,
  })

  const docTypeMutation = useMutation({
    mutationFn: (next: DocumentTypePref[]) =>
      patchPrintingSettings({ document_types: next }),
    onSuccess: (updated) => {
      queryClient.setQueryData(PRINTING_QUERY_KEY, updated)
    },
    onError: () => {
      toast.error(t.toasts.saveFailedTitle, {
        description: t.toasts.saveFailedDesc,
      })
    },
  })

  const warningsMutation = useMutation({
    mutationFn: (next: PrintWarningSettings) =>
      patchPrintingSettings({ print_warnings: next }),
    onSuccess: (updated) => {
      queryClient.setQueryData(PRINTING_QUERY_KEY, updated)
    },
    onError: () => {
      toast.error(t.toasts.saveFailedTitle, {
        description: t.toasts.saveFailedDesc,
      })
    },
  })

  if (surface !== 'admin') {
    return <PrintingPlaceholder surface={surface} />
  }

  if (isLoading) {
    return (
      <div className="rounded-lg border border-slate-200 bg-white p-6 text-sm text-slate-500">
        {t.states.loading}
      </div>
    )
  }
  if (isError || !data) {
    return (
      <div className="rounded-lg border border-rose-200 bg-rose-50 p-6 text-sm text-rose-700">
        {t.states.loadFailedPrefix}
        <code className="font-mono">/settings/printing</code>
        {t.states.loadFailedSuffix}
      </div>
    )
  }

  function updatePrintTo(docType: DocumentType, next: PrintTo) {
    const prevList = data!.document_types
    const nextList: DocumentTypePref[] = prevList.map((row) =>
      row.document_type === docType ? { ...row, print_to: next } : row,
    )
    docTypeMutation.mutate(nextList)
  }

  function openDocumentOptions(docType: DocumentType) {
    if (docType === 'label') return setModal('label')
    if (docType === 'packing_slip') return setModal('packing_slip')
    if (docType === 'order_summary') return setModal('order_summary')
    if (docType === 'return_label') {
      toast.info(t.toasts.comingSoon(t.documentTypes.return_label), {
        description: t.toasts.backendRatifyNote,
      })
      return
    }
    if (docType === 'pick_list') {
      toast.info(t.toasts.comingSoon(t.documentTypes.pick_list), {
        description: t.toasts.backendRatifyNote,
      })
      return
    }
    if (docType === 'shipment_manifest') {
      toast.info(t.toasts.comingSoon(t.documentTypes.shipment_manifest), {
        description: t.toasts.backendRatifyNote,
      })
      return
    }
  }

  // Render markdown-lite **bold** in the page description.
  const pageDescriptionParts = (USE_REAL_API ? t.browserPrintDescription : t.pageDescription).split(/(\*\*[^*]+\*\*)/g)

  return (
    <div className="space-y-6">
      {/* ============ Header + Supplies upsell ================== */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <header className="lg:col-span-2">
          <div className="flex items-start gap-2">
            <SettingsIcon className="mt-1 h-5 w-5 text-slate-400" />
            <div>
              <h2 className="text-lg font-semibold text-slate-900">
                {t.pageTitle}
              </h2>
              <p className="mt-1 text-sm text-slate-600">
                {pageDescriptionParts.map((part, i) => {
                  if (part.startsWith('**') && part.endsWith('**')) {
                    return <strong key={i}>{part.slice(2, -2)}</strong>
                  }
                  return <span key={i}>{part}</span>
                })}
              </p>
            </div>
            <button
              type="button"
              title={t.helpAriaLabel}
              className="ml-auto rounded-full p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
            >
              <Info className="h-4 w-4" />
            </button>
          </div>
        </header>

        {/* Supplies store mini card */}
        {!USE_REAL_API && <aside className="rounded-lg border border-slate-200 bg-slate-50 p-4">
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault()
              toast.info(t.toasts.suppliesStoreTitle, {
                description: t.toasts.suppliesStoreDesc,
              })
            }}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-700 hover:underline"
          >
            {t.supplies.storeLink}
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
          <p className="mt-1 text-xs text-slate-500">{t.supplies.storeHint}</p>
        </aside>}
      </div>

      {/* ============ Document Type table ============ */}
      <DataTableShell>
        <table>
          <thead>
            <tr>
              <th>{t.columns.documentType}</th>
              <th>{t.columns.printTo}</th>
              <th>{t.columns.documentFormat}</th>
              <th>{t.columns.options}</th>
            </tr>
          </thead>
          <tbody>
            {data.document_types.map((row) => (
              <tr key={row.document_type}>
                <td className="py-2.5 font-medium text-[var(--color-text)]">
                  {t.documentTypes[row.document_type]}
                </td>
                <td className="py-2.5">
                  {USE_REAL_API ? <span className="text-sm text-[var(--color-text-muted)]">{t.browserPrint}</span> : <select
                    value={row.print_to}
                    onChange={(e) =>
                      updatePrintTo(row.document_type, e.target.value as PrintTo)
                    }
                    className={
                      'min-w-[170px] cursor-pointer rounded-md border border-transparent bg-transparent px-1 py-1 text-sm font-medium text-brand-700 hover:bg-brand-50 focus:border-brand-300 focus:outline-none focus:ring-1 focus:ring-brand-200'
                    }
                  >
                    {PRINT_TO_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>}
                </td>
                <td className="py-2.5 text-[var(--color-text)]">
                  {row.document_format_display}
                </td>
                <td className="py-2.5">
                  {row.has_options ? (
                    <button
                      type="button"
                      onClick={() => openDocumentOptions(row.document_type)}
                      className="text-sm font-medium text-brand-700 hover:underline"
                    >
                      {t.columns.documentOptions}
                    </button>
                  ) : (
                    <span className="text-xs text-[var(--color-text-muted)]">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </DataTableShell>

      {/* ============ Print Warnings ============ */}
      {!USE_REAL_API && <section className="rounded-lg border border-slate-200 bg-white p-5">
        <SectionTitle hint={t.warnings.hint}>{t.warnings.title}</SectionTitle>
        <div className="divide-y divide-slate-100">
          <Toggle
            label={t.warnings.reprintPackingSlipLabel}
            description={t.warnings.reprintPackingSlipDescription}
            checked={data.print_warnings.reprint_packing_slips}
            onChange={(next) =>
              warningsMutation.mutate({
                ...data.print_warnings,
                reprint_packing_slips: next,
              })
            }
          />
          <Toggle
            label={t.warnings.reprintLabelsLabel}
            description={t.warnings.reprintLabelsDescription}
            checked={data.print_warnings.reprint_labels}
            onChange={(next) =>
              warningsMutation.mutate({
                ...data.print_warnings,
                reprint_labels: next,
              })
            }
          />
        </div>
      </section>}

      {/* ============ Supplies card — mock placeholder ============ */}
      {!USE_REAL_API && <NeedMoreLabelsCard />}

      {!USE_REAL_API && <p className="text-xs text-slate-400">{t.footerNote}</p>}

      {/* ============ Document Options modals ============ */}
      {modal === 'label' && data && (
        <LabelLayoutDialog
          initial={data.label_layout}
          onClose={() => setModal(null)}
          onSave={(next) => {
            patchPrintingSettings({ label_layout: next })
              .then((updated) => {
                queryClient.setQueryData(PRINTING_QUERY_KEY, updated)
                toast.success(t.toasts.savedTitle, {
                  description: t.toasts.labelLayoutSavedDesc,
                })
                setModal(null)
              })
              .catch(() =>
                toast.error(t.toasts.saveFailedTitle, {
                  description: t.toasts.saveFailedDesc,
                }),
              )
          }}
        />
      )}
      {modal === 'packing_slip' && data && (
        <PackingSlipDialog
          initial={data.packing_slip}
          onClose={() => setModal(null)}
          onSave={(next) => {
            patchPrintingSettings({ packing_slip: next })
              .then((updated) => {
                queryClient.setQueryData(PRINTING_QUERY_KEY, updated)
                toast.success(t.toasts.savedTitle, {
                  description: t.toasts.packingSlipSavedDesc,
                })
                setModal(null)
              })
              .catch(() =>
                toast.error(t.toasts.saveFailedTitle, {
                  description: t.toasts.saveFailedDesc,
                }),
              )
          }}
        />
      )}
      {modal === 'order_summary' && data && (
        <OrderSummaryDialog
          initial={data.order_summary}
          onClose={() => setModal(null)}
          onSave={(next) => {
            patchPrintingSettings({ order_summary: next })
              .then((updated) => {
                queryClient.setQueryData(PRINTING_QUERY_KEY, updated)
                toast.success(t.toasts.savedTitle, {
                  description: t.toasts.orderSummarySavedDesc,
                })
                setModal(null)
              })
              .catch(() =>
                toast.error(t.toasts.saveFailedTitle, {
                  description: t.toasts.saveFailedDesc,
                }),
              )
          }}
        />
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// "Need More Labels?" upsell card with 3 thumbnail placeholders.
// ---------------------------------------------------------------------------

const SUPPLY_PLACEHOLDERS: { id: string; name: string; price: string }[] = [
  { id: 'sup-4x6-roll', name: '4" × 6" Thermal Roll', price: '$24.00' },
  { id: 'sup-sheets', name: '8.5" × 11" Label Sheets', price: '$18.50' },
  { id: 'sup-clear-tape', name: 'Clear Shipping Tape', price: '$9.99' },
]

function NeedMoreLabelsCard() {
  const t = useT(printingSetupI18n)
  return (
    <section className="rounded-lg border border-slate-200 bg-white p-5">
      <SectionTitle hint={t.supplies.needMoreHint}>
        {t.supplies.needMoreTitle}
      </SectionTitle>
      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
        {SUPPLY_PLACEHOLDERS.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() =>
              toast.info(`${s.name} ${t.toasts.crossSellTitleSuffix}`, {
                description: t.toasts.crossSellDescSuffix,
              })
            }
            className="flex flex-col items-start gap-2 rounded-lg border border-slate-200 p-3 text-left transition hover:border-brand-300 hover:bg-brand-50/40"
          >
            <div className="flex h-20 w-full items-center justify-center rounded-md bg-slate-100 text-slate-300">
              <Printer className="h-7 w-7" />
            </div>
            <div className="text-sm font-medium text-slate-800">{s.name}</div>
            <div className="text-xs font-semibold text-emerald-700">{s.price}</div>
          </button>
        ))}
      </div>
    </section>
  )
}

// ---------------------------------------------------------------------------
// Generic modal shell — wider than the default Dialog (640-720px).
// ---------------------------------------------------------------------------

const DIALOG_CLS_LG =
  'max-w-[720px] w-[calc(100vw-2rem)] max-h-[88vh] overflow-y-auto'
const DIALOG_CLS_MD =
  'max-w-[640px] w-[calc(100vw-2rem)] max-h-[88vh] overflow-y-auto'

function DialogInfoBanner({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-900">
      <Info className="mr-1.5 inline h-3.5 w-3.5 align-text-bottom" />
      {children}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Label Layout Dialog — the rich one. 4"x6" tab selector + 2 layout cards +
// DPI + batch + sort + 3 messages + cutoff.
// ---------------------------------------------------------------------------

/** The label templates the sample can draw, in the order the dialog offers them. */
const SAMPLE_CARRIERS: readonly SampleCarrier[] = ['usps', 'ups']

function LabelLayoutDialog({
  initial,
  onClose,
  onSave,
}: {
  initial: LabelLayoutSettings
  onClose: () => void
  onSave: (next: LabelLayoutSettings) => void
}) {
  const t = useT(printingSetupI18n)
  const [state, setState] = useState<LabelLayoutSettings>(initial)
  // Which carrier's sample is being drawn right now, so its own button reads
  // "Drawing…" while the other stays clickable-looking but disabled.
  const [drawing, setDrawing] = useState<SampleCarrier | null>(null)
  const mountedRef = useRef(true)
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  function showSample(carrier: SampleCarrier) {
    setDrawing(carrier)
    void openPrintingPreview('label', state, carrier).then((result) => {
      // The dialog may be gone by the time the PDF arrives.
      if (!mountedRef.current) return
      setDrawing(null)
      if (result === 'failed') {
        toast.error(t.toasts.previewFailedTitle, {
          description: t.toasts.previewFailedDesc,
        })
      }
    })
  }

  // When user switches the active size tab we update the size but leave
  // existing layout intact (mock; M3 backend handles layout × size matrix).
  function switchSize(next: LabelSize) {
    setState((prev) => ({ ...prev, size: next }))
  }

  const layoutChoices: {
    value: LabelLayoutSettings['layout']
    title: string
    printerCategory: string
    sizeRequirement: LabelSize
    hasPackingSlip: boolean
  }[] = useMemo(
    () => [
      {
        value: '4x6_label_only',
        title: t.dialogs.labelLayout.layoutLabelOnly,
        printerCategory: t.dialogs.labelLayout.labelPrinterCategory,
        sizeRequirement: '4x6',
        hasPackingSlip: false,
      },
      {
        value: '4x6_label_with_packing_slip',
        title: t.dialogs.labelLayout.layoutLabelWithSlip,
        printerCategory: t.dialogs.labelLayout.labelPrinterCategory,
        sizeRequirement: '4x6',
        hasPackingSlip: true,
      },
    ],
    [t],
  )

  const [activeMessage, setActiveMessage] = useState<0 | 1 | 2>(0)

  function setLabelMessage(index: 0 | 1 | 2, value: string) {
    setState((prev) => {
      const msgs: [string, string, string] = [...prev.label_messages] as [
        string,
        string,
        string,
      ]
      msgs[index] = value.slice(0, MESSAGE_TEMPLATE_MAX)
      return { ...prev, label_messages: msgs }
    })
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className={DIALOG_CLS_LG}>
        <DialogHeader>
          <DialogTitle>{t.dialogs.labelLayout.title}</DialogTitle>
        </DialogHeader>

        <DialogInfoBanner>{t.dialogs.info}</DialogInfoBanner>

        {/* Size tab bar */}
        <div className="flex flex-wrap gap-1 rounded-md border border-slate-200 bg-slate-50 p-1">
          {LABEL_SIZE_TABS.map((tab) => {
            const active = tab.value === state.size
            return (
              <button
                key={tab.value}
                type="button"
                onClick={() => switchSize(tab.value)}
                className={
                  'flex-1 rounded px-3 py-1.5 text-sm font-medium transition-colors ' +
                  (active
                    ? 'bg-white text-slate-900 shadow-sm ring-1 ring-slate-200'
                    : 'text-slate-500 hover:text-slate-700')
                }
              >
                {tab.label}
              </button>
            )
          })}
        </div>

        {/* Layout cards (visible for 4x6 size; other sizes show notice) */}
        {state.size === '4x6' ? (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {layoutChoices.map((choice) => {
              const active = state.layout === choice.value
              return (
                <button
                  key={choice.value}
                  type="button"
                  onClick={() =>
                    setState((prev) => ({ ...prev, layout: choice.value }))
                  }
                  className={
                    'relative rounded-lg border-2 p-3 text-left transition ' +
                    (active
                      ? 'border-brand-500 bg-brand-50 ring-2 ring-brand-200'
                      : 'border-slate-200 hover:border-slate-300 bg-white')
                  }
                >
                  {active && (
                    <span className="absolute right-2 top-2 inline-flex h-5 w-5 items-center justify-center rounded-full bg-brand-600 text-white">
                      <Check className="h-3 w-3" />
                    </span>
                  )}
                  <div className="text-sm font-semibold text-slate-900">
                    {choice.title}
                  </div>
                  <div className="mt-0.5 text-xs text-slate-500">
                    {choice.printerCategory}
                  </div>
                  {/* Thumbnail row */}
                  <div className="mt-3 flex items-end gap-2">
                    <div className="h-16 w-12 rounded bg-slate-200 ring-1 ring-slate-300" />
                    {choice.hasPackingSlip && (
                      <div className="h-20 w-16 rounded bg-slate-100 ring-1 ring-slate-300" />
                    )}
                  </div>
                </button>
              )
            })}
          </div>
        ) : (
          <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-center text-sm text-slate-500">
            {t.dialogs.labelLayout.sizeNotice(state.size.toUpperCase())}
          </div>
        )}

        <div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-slate-500">
              {t.dialogs.labelLayout.sampleLink}
            </span>
            {SAMPLE_CARRIERS.map((carrier) => (
              <Button
                key={carrier}
                variant="outline"
                size="sm"
                disabled={drawing !== null}
                onClick={() => showSample(carrier)}
              >
                {drawing === carrier
                  ? t.dialogs.labelLayout.sampleDrawing
                  : t.dialogs.labelLayout.sampleCarriers[carrier]}
              </Button>
            ))}
          </div>
          <p className="mt-1.5 text-xs text-slate-500">
            {t.dialogs.labelLayout.sampleNote}
          </p>
        </div>

        {/* Label DPI */}
        <section>
          <SectionTitle>{t.dialogs.labelLayout.labelDpiTitle}</SectionTitle>
          <select
            value={state.dpi}
            onChange={(e) =>
              setState((prev) => ({
                ...prev,
                dpi: e.target.value as LabelLayoutSettings['dpi'],
              }))
            }
            className={SELECT_CLS}
          >
            {LABEL_DPI_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-slate-500">
            {t.dialogs.labelLayout.labelDpiNote}
          </p>
        </section>

        {/* Batch summary */}
        <section>
          <SectionTitle>{t.dialogs.labelLayout.batchSummaryTitle}</SectionTitle>
          <select
            value={state.batch_summary}
            onChange={(e) =>
              setState((prev) => ({
                ...prev,
                batch_summary: e.target.value as LabelLayoutSettings['batch_summary'],
              }))
            }
            className={SELECT_CLS}
          >
            {BATCH_SUMMARY_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </section>

        {/* Order labels by */}
        <section>
          <SectionTitle hint={t.dialogs.labelLayout.orderLabelsByHint}>
            {t.dialogs.labelLayout.orderLabelsByTitle}
          </SectionTitle>
          <select
            value={state.order_labels_by}
            onChange={(e) =>
              setState((prev) => ({
                ...prev,
                order_labels_by: e.target
                  .value as LabelLayoutSettings['order_labels_by'],
              }))
            }
            className={SELECT_CLS}
          >
            {ORDER_LABELS_BY_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </section>

        {/* Label messages */}
        <section>
          <SectionTitle hint={t.dialogs.labelLayout.labelMessagesHint}>
            {t.dialogs.labelLayout.labelMessagesTitle}
          </SectionTitle>
          <div className="space-y-2">
            {[0, 1, 2].map((i) => {
              const idx = i as 0 | 1 | 2
              const value = state.label_messages[idx]
              return (
                <div key={i}>
                  <FieldLabel>
                    {t.dialogs.labelLayout.messageNumber(i + 1)}
                  </FieldLabel>
                  <input
                    type="text"
                    value={value}
                    onChange={(e) => setLabelMessage(idx, e.target.value)}
                    onFocus={() => setActiveMessage(idx)}
                    maxLength={MESSAGE_TEMPLATE_MAX}
                    placeholder={
                      i === 0 ? t.dialogs.labelLayout.messagePlaceholder : ''
                    }
                    className={INPUT_CLS}
                  />
                </div>
              )
            })}
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            <span className="text-xs text-slate-500">
              {t.dialogs.labelLayout.tokensLabel}
            </span>
            {LABEL_MESSAGE_TOKENS.map((token) => (
              <button
                key={token}
                type="button"
                onClick={() =>
                  setLabelMessage(
                    activeMessage,
                    `${state.label_messages[activeMessage]} ${token}`.trim(),
                  )
                }
                className="rounded border border-slate-200 px-1.5 py-0.5 font-mono text-[11px] text-slate-600 hover:border-brand-400 hover:text-brand-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
              >
                {token}
              </button>
            ))}
          </div>
          <p className="mt-2 text-xs text-slate-500">
            {t.dialogs.labelLayout.messagesNote}
          </p>
        </section>

        {/* Shipping cutoff */}
        <section>
          <SectionTitle>{t.dialogs.labelLayout.shippingCutoffTitle}</SectionTitle>
          <div className="flex flex-wrap items-center gap-3">
            <label className="flex cursor-pointer items-center gap-2">
              <input
                type="checkbox"
                checked={state.shipping_cutoff_enabled}
                onChange={(e) =>
                  setState((prev) => ({
                    ...prev,
                    shipping_cutoff_enabled: e.target.checked,
                  }))
                }
                className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-400"
              />
              <span className="text-sm text-slate-700">
                {t.dialogs.labelLayout.shippingCutoffLabel}
              </span>
            </label>
            <input
              type="text"
              value={state.shipping_cutoff_time}
              disabled={!state.shipping_cutoff_enabled}
              onChange={(e) =>
                setState((prev) => ({
                  ...prev,
                  shipping_cutoff_time: e.target.value,
                }))
              }
              className={INPUT_CLS + ' w-32 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400'}
            />
          </div>
          <p className="mt-1 text-xs text-slate-500">
            {t.dialogs.labelLayout.cutoffNote}
          </p>
        </section>

        <DialogFooter>
          <Button
            variant="outline"
            size="sm"
            className="sm:mr-auto"
            disabled={drawing !== null}
            onClick={() => showSample('usps')}
          >
            {t.dialogs.labelLayout.preview}
          </Button>
          <Button variant="primary" size="sm" onClick={() => onSave(state)}>
            {t.dialogs.labelLayout.saveSettings}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ---------------------------------------------------------------------------
// Packing Slip Dialog
// ---------------------------------------------------------------------------

function PackingSlipDialog({
  initial,
  onClose,
  onSave,
}: {
  initial: PackingSlipSettings
  onClose: () => void
  onSave: (next: PackingSlipSettings) => void
}) {
  const t = useT(printingSetupI18n)
  const [state, setState] = useState<PackingSlipSettings>(initial)
  const [previewing, setPreviewing] = useState(false)
  const mountedRef = useRef(true)
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className={DIALOG_CLS_LG}>
        <DialogHeader>
          <DialogTitle>{t.dialogs.packingSlip.title}</DialogTitle>
        </DialogHeader>

        <DialogInfoBanner>{t.dialogs.info}</DialogInfoBanner>

        <p className="text-sm text-slate-600">{t.dialogs.packingSlip.lead}</p>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <FieldLabel>{t.dialogs.packingSlip.formatField}</FieldLabel>
            <select
              value={state.format}
              onChange={(e) =>
                setState((prev) => ({
                  ...prev,
                  format: e.target.value as PackingSlipSettings['format'],
                }))
              }
              className={SELECT_CLS}
            >
              {PACKING_SLIP_FORMAT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <FieldLabel>{t.dialogs.packingSlip.sortItemsField}</FieldLabel>
            <select
              value={state.sort_items_by}
              onChange={(e) =>
                setState((prev) => ({
                  ...prev,
                  sort_items_by: e.target
                    .value as PackingSlipSettings['sort_items_by'],
                }))
              }
              className={SELECT_CLS}
            >
              {SORT_ITEMS_BY_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <section className="rounded-md border border-slate-200">
          <div className="divide-y divide-slate-100 px-4">
            <Toggle
              label={t.dialogs.packingSlip.toggles.storeLogoLabel}
              description={t.dialogs.packingSlip.toggles.storeLogoDescription}
              checked={state.show_store_logo}
              onChange={(next) =>
                setState((prev) => ({ ...prev, show_store_logo: next }))
              }
            />
            <Toggle
              label={t.dialogs.packingSlip.toggles.itemPricesLabel}
              description={t.dialogs.packingSlip.toggles.itemPricesDescription}
              checked={state.show_item_prices}
              onChange={(next) =>
                setState((prev) => ({ ...prev, show_item_prices: next }))
              }
            />
            <Toggle
              label={t.dialogs.packingSlip.toggles.itemSkusLabel}
              description={t.dialogs.packingSlip.toggles.itemSkusDescription}
              checked={state.show_item_skus}
              onChange={(next) =>
                setState((prev) => ({ ...prev, show_item_skus: next }))
              }
            />
            <Toggle
              label={t.dialogs.packingSlip.toggles.scanToViewLabel}
              description={t.dialogs.packingSlip.toggles.scanToViewDescription}
              checked={state.show_scan_to_view}
              onChange={(next) =>
                setState((prev) => ({ ...prev, show_scan_to_view: next }))
              }
            />
            <Toggle
              label={t.dialogs.packingSlip.toggles.itemLocationsLabel}
              description={
                t.dialogs.packingSlip.toggles.itemLocationsDescription
              }
              checked={state.show_item_locations}
              onChange={(next) =>
                setState((prev) => ({ ...prev, show_item_locations: next }))
              }
            />
            <Toggle
              label={t.dialogs.packingSlip.toggles.discountsLabel}
              description={t.dialogs.packingSlip.toggles.discountsDescription}
              checked={state.show_discounts}
              onChange={(next) =>
                setState((prev) => ({ ...prev, show_discounts: next }))
              }
            />
            <Toggle
              label={t.dialogs.packingSlip.toggles.zeroQtyLabel}
              description={t.dialogs.packingSlip.toggles.zeroQtyDescription}
              checked={state.show_zero_qty}
              onChange={(next) =>
                setState((prev) => ({ ...prev, show_zero_qty: next }))
              }
            />
            <Toggle
              label={t.dialogs.packingSlip.toggles.returnQrLabel}
              description={t.dialogs.packingSlip.toggles.returnQrDescription}
              checked={state.show_return_qr}
              onChange={(next) =>
                setState((prev) => ({ ...prev, show_return_qr: next }))
              }
            />
          </div>
        </section>

        <p className="text-xs text-slate-500">
          {t.dialogs.packingSlip.previewNote}
        </p>

        <DialogFooter>
          <Button
            variant="outline"
            size="sm"
            className="sm:mr-auto"
            disabled={previewing}
            onClick={() => {
              setPreviewing(true)
              void openPrintingPreview('packing_slip', state).then(
                (result) => {
                  // The dialog may be gone by the time the PDF arrives.
                  if (!mountedRef.current) return
                  setPreviewing(false)
                  if (result === 'failed') {
                    toast.error(t.toasts.previewFailedTitle, {
                      description: t.toasts.previewFailedDesc,
                    })
                  }
                },
              )
            }}
          >
            {t.dialogs.packingSlip.preview}
          </Button>
          <Button variant="outline" size="sm" onClick={onClose}>
            {t.dialogs.packingSlip.cancel}
          </Button>
          <Button variant="primary" size="sm" onClick={() => onSave(state)}>
            {t.dialogs.packingSlip.saveSettings}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ---------------------------------------------------------------------------
// Order Summary Dialog
// ---------------------------------------------------------------------------

function OrderSummaryDialog({
  initial,
  onClose,
  onSave,
}: {
  initial: OrderSummarySettings
  onClose: () => void
  onSave: (next: OrderSummarySettings) => void
}) {
  const t = useT(printingSetupI18n)
  const [state, setState] = useState<OrderSummarySettings>(initial)

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className={DIALOG_CLS_MD}>
        <DialogHeader>
          <DialogTitle>{t.dialogs.orderSummary.title}</DialogTitle>
        </DialogHeader>

        <DialogInfoBanner>{t.dialogs.info}</DialogInfoBanner>

        <p className="text-sm text-slate-600">{t.dialogs.orderSummary.lead}</p>

        <div>
          <FieldLabel>{t.dialogs.orderSummary.sortOrdersByField}</FieldLabel>
          <select
            value={state.sort_orders_by}
            onChange={(e) =>
              setState((prev) => ({
                ...prev,
                sort_orders_by: e.target
                  .value as OrderSummarySettings['sort_orders_by'],
              }))
            }
            className={SELECT_CLS}
          >
            {ORDER_SUMMARY_SORT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <section className="rounded-md border border-slate-200 px-4">
          <div className="divide-y divide-slate-100">
            <CheckboxRow
              label={t.dialogs.orderSummary.hideImages}
              checked={state.hide_images}
              onChange={(next) =>
                setState((prev) => ({ ...prev, hide_images: next }))
              }
            />
            <CheckboxRow
              label={t.dialogs.orderSummary.useProductSku}
              checked={state.use_product_sku}
              onChange={(next) =>
                setState((prev) => ({ ...prev, use_product_sku: next }))
              }
            />
            <CheckboxRow
              label={t.dialogs.orderSummary.hideDetails}
              checked={state.hide_details}
              onChange={(next) =>
                setState((prev) => ({ ...prev, hide_details: next }))
              }
            />
            <CheckboxRow
              label={t.dialogs.orderSummary.includeScanToView}
              checked={state.include_scan_to_view}
              onChange={(next) =>
                setState((prev) => ({ ...prev, include_scan_to_view: next }))
              }
            />
          </div>
        </section>

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose}>
            {t.dialogs.orderSummary.cancel}
          </Button>
          <Button variant="primary" size="sm" onClick={() => onSave(state)}>
            {t.dialogs.orderSummary.saveSettings}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
